// Pixel art drawn as code. Each row is a string; every character maps to a palette color and '.' is empty.

export type Sprite = {
    width: number
    height: number
    palette: Record<string, string>
    rows: string[]
}

const sprite = (palette: Record<string, string>, rows: string[]): Sprite => ({
    width: Math.max(...rows.map((row) => row.length)),
    height: rows.length,
    palette,
    rows
})

/* Fire, generated from a height per column so the tongues stay round and layered. */

const FIRE_PALETTE: Record<string, string> = {
    R: '#a3220d',
    r: '#e2461b',
    o: '#ff7a1a',
    O: '#ffa23a',
    y: '#ffd23f',
    Y: '#fff0a3',
    w: '#fffbea'
}

function flame(heights: number[], height: number, sparks: Array<[number, number]> = []): Sprite {
    const width = heights.length
    const heightAt = (x: number) => (x < 0 || x >= width ? 0 : heights[x])
    const lowestAround = (x: number, radius: number) => {
        let lowest = Infinity

        for (let i = x - radius; i <= x + radius; i++) lowest = Math.min(lowest, heightAt(i))

        return lowest
    }

    const grid: string[][] = []

    for (let row = 0; row < height; row++) {
        const y = height - 1 - row
        const line: string[] = []

        for (let x = 0; x < width; x++) {
            const h = heights[x]
            let color = '.'

            if (y < h) {
                const near = lowestAround(x, 1)
                const mid = lowestAround(x, 2)
                const far = lowestAround(x, 3)

                if (y < far - 9) color = 'w'
                else if (y < mid - 7) color = 'Y'
                else if (y < mid - 5) color = 'y'
                else if (y < near - 3) color = 'O'
                else if (y < near - 1) color = 'o'
                else if (y < h - 1) color = 'r'
                else color = 'R'
            }

            line.push(color)
        }

        grid.push(line)
    }

    for (const [x, y] of sparks) {
        if (grid[y] && grid[y][x] === '.') grid[y][x] = 'y'
    }

    return { width, height, palette: FIRE_PALETTE, rows: grid.map((line) => line.join('')) }
}

export const CAMPFIRE_A = flame(
    [2, 5, 8, 7, 11, 14, 12, 16, 19, 15, 13, 17, 21, 18, 14, 16, 19, 13, 10, 11, 7, 3],
    23,
    [[4, 6], [17, 0], [20, 9]]
)

export const CAMPFIRE_B = flame(
    [3, 6, 5, 9, 12, 10, 14, 17, 14, 18, 20, 15, 13, 17, 20, 15, 12, 14, 9, 7, 8, 2],
    23,
    [[6, 4], [11, 1], [2, 12]]
)

export const LOGO_FIRE_A = flame([2, 5, 4, 8, 11, 9, 13, 15, 12, 10, 12, 8, 5, 2], 16, [[1, 5]])
export const LOGO_FIRE_B = flame([3, 4, 7, 6, 10, 12, 11, 14, 15, 11, 9, 10, 6, 3], 16, [[12, 3]])

function campfireLogs(width: number): Sprite {
    const inner = width - 2
    const fill = (pick: (i: number) => string) => Array.from({ length: inner }, (_, i) => pick(i)).join('')

    return sprite(
        {
            k: '#1f130b',
            b: '#6b4426',
            B: '#4d2f19',
            l: '#8f5f35',
            a: '#3a2416'
        },
        [
            `.${'k'.repeat(inner)}.`,
            `k${fill((i) => (i % 7 === 3 ? 'l' : 'b'))}k`,
            `k${fill((i) => (i % 6 === 1 ? 'B' : 'b'))}k`,
            `k${fill((i) => (i % 9 === 5 ? 'a' : 'B'))}k`,
            `.${'k'.repeat(inner)}.`
        ]
    )
}

export const CAMPFIRE_LOGS = campfireLogs(26)

/* Items */

export const COIN = sprite(
    {
        o: '#7a4a00',
        l: '#ffe680',
        m: '#c98a00',
        g: '#f5bf1f',
        f: '#8f5c00',
        h: '#fff6c4'
    },
    [
        '.....oooooo.....',
        '...oolllllloo...',
        '..ollggggggllo..',
        '.olhhggggggggmo.',
        '.olhgggggggggmo.',
        'olgggffggffgggmo',
        'olgggffggffgggmo',
        'olgggggffgggggmo',
        'olggggffffggggmo',
        'olggggffffggggmo',
        'olggggfggfggggmo',
        '.olggggggggggmo.',
        '.olggggggggggmo.',
        '..ommggggggmmo..',
        '...oommmmmmoo...',
        '.....oooooo.....'
    ]
)

export const DIAMOND = sprite(
    {
        o: '#0b3d3a',
        w: '#e9fffb',
        l: '#9ef7ea',
        c: '#45e0d0',
        m: '#22b5a8',
        d: '#157a72'
    },
    [
        '....oooooooo....',
        '...owwlllccco...',
        '..owwllllcccmo..',
        '.owllllllcccmmo.',
        '.odlllcccmmmddo.',
        '..odllcccmmmdo..',
        '...odlccmmmdo...',
        '....odccmmdo....',
        '.....odcmdo.....',
        '......oddo......',
        '.......oo.......'
    ]
)

export const TNT = sprite(
    {
        k: '#1a1110',
        h: '#f2634a',
        r: '#db3b25',
        R: '#9e2316',
        w: '#f4efe8',
        g: '#cfc8c0',
        b: '#1d1d1d'
    },
    [
        'kkkkkkkkkkkkkkkk',
        'khhRRhhRRhhRRhhk',
        'krrRRrrRRrrRRrrk',
        'kRRrrRRrrRRrrRRk',
        'kRRrrRRrrRRrrRRk',
        'kwwwwwwwwwwwwwwk',
        'kwbbbwbwwbwbbbwk',
        'kwwbwwbbwbwwbwwk',
        'kwwbwwbwbbwwbwwk',
        'kwwbwwbwwbwwbwwk',
        'kggggggggggggggk',
        'krrRRrrRRrrRRrrk',
        'krrRRrrRRrrRRrrk',
        'kRRrrRRrrRRrrRRk',
        'kRRrrRRrrRRrrRRk',
        'kkkkkkkkkkkkkkkk'
    ]
)

const DIE_PALETTE = {
    o: '#2b2420',
    w: '#fbf6ef',
    s: '#d8cfc4',
    p: '#e0322b'
}

export const DIE_FIVE = sprite(DIE_PALETTE, [
    '.oooooooooo.',
    'owwwwwwwwwso',
    'owppwwwwppso',
    'owppwwwwppso',
    'owwwwwwwwwso',
    'owwwwppwwwso',
    'owwwwppwwwso',
    'owwwwwwwwwso',
    'owppwwwwppso',
    'owppwwwwppso',
    'osssssssssso',
    '.oooooooooo.'
])

export const DIE_THREE = sprite(DIE_PALETTE, [
    '.oooooooooo.',
    'owwwwwwwwwso',
    'owwwwwwwppso',
    'owwwwwwwppso',
    'owwwwwwwwwso',
    'owwwwppwwwso',
    'owwwwppwwwso',
    'owwwwwwwwwso',
    'owppwwwwwwso',
    'owppwwwwwwso',
    'osssssssssso',
    '.oooooooooo.'
])

export const DONUT = sprite(
    {
        o: '#4a2511',
        d: '#e0a35f',
        D: '#b8743a',
        p: '#ff7eb6',
        P: '#ffb3d4',
        k: '#e5508f',
        '1': '#ffe14d',
        '2': '#4fd7ff',
        '3': '#ffffff',
        '4': '#7cff6b'
    },
    [
        '.....oooooo.....',
        '...ooPPPPPPoo...',
        '..oPPpppppppPo..',
        '.oPp1ppp3pp2pko.',
        '.opp2pppppp1pko.',
        'opp2pppppp1pppko',
        'op3ppppooppp4pko',
        'oppp1po..oppppko',
        'op2pppo..op3ppko',
        'oppppppooppp1pko',
        'odpp4pppppp2pkDo',
        '.odkppkppkppkDo.',
        '.oddkddkddkdDDo.',
        '..odddddddDDDo..',
        '...ooDDDDDDoo...',
        '.....oooooo.....'
    ]
)

export const ROCKET = sprite(
    {
        o: '#2a1a12',
        w: '#fbf2e8',
        g: '#c9c2b8',
        G: '#8f877d',
        r: '#e0342a',
        R: '#a12118',
        s: '#d9cfc2',
        b: '#7a4a24'
    },
    [
        '...o...',
        '..owo..',
        '.owggo.',
        '.oggGo.',
        'orrrrRo',
        'orrrrRo',
        'owwwwso',
        'owwwwso',
        'orrrrRo',
        'orrrrRo',
        'owwwwso',
        'owwwwso',
        'orrrrRo',
        'ooooooo',
        '..obo..',
        '..obo..'
    ]
)

export const ROCKET_FLAME = sprite(
    {
        y: '#ffd23f',
        Y: '#fff0a3',
        w: '#fffbea',
        o: '#ff7a1a',
        r: '#e2461b'
    },
    [
        '..yYy..',
        '.yYwYy.',
        '.oyYyo.',
        '..oyo..',
        '..ror..',
        '...r...'
    ]
)

function burst(size: number): Sprite {
    const center = (size - 1) / 2
    const rows: string[] = []

    for (let y = 0; y < size; y++) {
        let line = ''

        for (let x = 0; x < size; x++) {
            const dx = x - center
            const dy = y - center
            const distance = Math.sqrt(dx * dx + dy * dy)
            const angle = Math.atan2(dy, dx)
            const reach = 4.2 + 3.8 * Math.pow(Math.abs(Math.cos(angle * 4)), 3)

            if (distance > reach) line += '.'
            else if (distance < 1.6) line += 'w'
            else if (distance < 3) line += 'Y'
            else if (distance < 4.4) line += 'y'
            else if (distance < 6) line += 'o'
            else line += 'r'
        }

        rows.push(line)
    }

    return { width: size, height: size, palette: FIRE_PALETTE, rows }
}

export const EXPLOSION = burst(19)

/* Player avatars, 8×8 faces */

export const AVATARS: Record<string, { label: string, sprite: Sprite }> = {
    miner: {
        label: 'Miner',
        sprite: sprite(
            { h: '#3d2a1c', H: '#2e1f14', s: '#c8956d', S: '#b07c58', w: '#ffffff', b: '#4c5aa8', n: '#9a6347', m: '#6b3f2a' },
            ['hHhhhhHh', 'hhhhhhhh', 'hsssssSh', 'ssssssss', 'swbssbws', 'sssnnsss', 'ssmssmss', 'ssmmmmss']
        )
    },
    explorer: {
        label: 'Explorer',
        sprite: sprite(
            { h: '#e2752a', H: '#c75e1c', s: '#f0c39b', w: '#ffffff', e: '#3c8a3c', m: '#c98a6a' },
            ['hhhhhhhh', 'hhHhhhhh', 'hhssssss', 'hsssssss', 'hwessews', 'hsssssss', 'sssmmsss', 'ssssssss']
        )
    },
    creeper: {
        label: 'Creeper',
        sprite: sprite(
            { g: '#5cbf3a', l: '#8be05c', m: '#48a42b', d: '#1d3814' },
            ['glgmglgl', 'lgmglglg', 'gddggddg', 'gddggddg', 'gggddggg', 'ggddddgg', 'ggddddgg', 'ggdggdgg']
        )
    },
    zombie: {
        label: 'Zombie',
        sprite: sprite(
            { z: '#4f8a3c', Z: '#3b6d2c', k: '#1d2a18', h: '#2c4720' },
            ['hhhhhhhh', 'hZhhZhhZ', 'zzzzzzzz', 'zZzzzzZz', 'zkkzzkkz', 'zzzZZzzz', 'zzZzzZzz', 'zzzzzzzz']
        )
    },
    skeleton: {
        label: 'Skeleton',
        sprite: sprite(
            { b: '#d6d6cf', B: '#b3b3aa', e: '#2a2a2a', m: '#4a4a46' },
            ['bbbbbbbb', 'bBbbbbBb', 'bbbbbbbb', 'beebbeeb', 'beebbeeb', 'bbbeebbb', 'bmmmmmmb', 'bbbbbbbb']
        )
    },
    enderman: {
        label: 'Enderman',
        sprite: sprite(
            { k: '#141414', K: '#262626', p: '#cc7aff', P: '#f0c6ff' },
            ['kkkkkkkk', 'kKkkkkKk', 'kkkkkkkk', 'kkkkkkkk', 'pPpkkpPp', 'kkkkkkkk', 'kKkkkkKk', 'kkkkkkkk']
        )
    },
    pig: {
        label: 'Pig',
        sprite: sprite(
            { p: '#f1a3ab', P: '#e2858f', n: '#f8c6cb', h: '#8b3a49', e: '#1f1f1f', w: '#ffffff' },
            ['pppppppp', 'pPppppPp', 'pppppppp', 'pewppwep', 'ppnnnnpp', 'pphnnhpp', 'ppnnnnpp', 'pppppppp']
        )
    }
}

export const AVATAR_IDS = Object.keys(AVATARS)

export const DEFAULT_AVATAR = 'miner'

export function isAvatarId(value: unknown): value is string {
    return typeof value === 'string' && value in AVATARS
}
